import {Button, Dropdown, Select, Spin} from 'antd'
import {
    course_enrollment_assignClass_Api,
    course_enrollment_resetToPending_Api,
    course_enrollment_resolveIdentity_Api,
    course_enrollment_searchCandidates_Api,
    course_enrollment_startReview_Api,
} from 'apis'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {BsArrowCounterclockwise} from 'react-icons/bs'
import {IoCheckmarkCircle} from 'react-icons/io5'
import {MdMoreVert} from 'react-icons/md'
import {NullAsDefault} from 'views/Shared'
import {CandidateCard} from './CandidateCard'
import {EnrollmentInfoPanel} from './EnrollmentInfoPanel'
import {EnrollmentStepProgress} from './EnrollmentStepProgress'
import {StudentDetailModal} from './StudentDetailModal'
import {SelectWidget} from "../../../AppLocals/custom/ReactSelectComponents";

// ─── Step resolver ────────────────────────────────────────────────────────────
function resolveStep(status) {
    if (status === 9) return 'completed'
    if (status === 2) return 'assign_class'
    return 'identity_review'
}

// ─── Layout wrapper ───────────────────────────────────────────────────────────
const ReviewLayout = ({infoPanel, actionPanel}) => (
    <div className="flex flex-col flex-1 min-h-0">
        {/* Info panel */}
        <div className="border-b border-color-50">
            <div className="p-4">
                {infoPanel}
            </div>
        </div>
        {/* Action panel */}
        <div className="flex-1 overflow-y-auto">
            <div className="p-4 flex flex-col gap-4">
                {actionPanel}
            </div>
        </div>
    </div>
)

// ─── Main component ───────────────────────────────────────────────────────────
export const EnrollmentReviewDrawer = ({
                                           enrollment,
                                           open,
                                           onClose = NullAsDefault,
                                           onCompleted = NullAsDefault,
                                           courseClasses = [],
                                       }) => {
    const t = useTranslate()
    const [currentEnrollment, setCurrentEnrollment] = useState(enrollment)
    const [candidates, setCandidates] = useState([])
    const [selectedClassId, setSelectedClassId] = useState(null)
    const [resolvingStudentId, setResolvingStudentId] = useState(null)
    const [viewDetailStudent, setViewDetailStudent] = useState(null)
    const triggeredRef = useRef(new Set())

    useEffect(() => {
        setCurrentEnrollment(enrollment)
        setCandidates([])
        setSelectedClassId(null)
        triggeredRef.current.clear()
    }, [enrollment])

    const enrollmentId = currentEnrollment?.id
    const currentStep = useMemo(() => resolveStep(currentEnrollment?.status), [currentEnrollment])

    // ─── API actions ──────────────────────────────────────────────────────────

    const {handleAsyncAction: startReview, isLoading: startingReview} = useAsyncAction({
        apiInfo: course_enrollment_startReview_Api,
        onSuccess: (result, meta) => {
            const updated = _.get(meta, 'response.data')
            if (updated) setCurrentEnrollment(updated)
        },
    })

    const {handleAsyncAction: searchCandidates, isLoading: searching} = useAsyncAction({
        apiInfo: course_enrollment_searchCandidates_Api,
        onSuccess: (result, meta) => {
            const data = _.get(meta, 'response.data', [])
            setCandidates(Array.isArray(data) ? data.slice(0, 5) : [])
        },
    })

    const {handleAsyncAction: resolveIdentity, isLoading: resolvingIdentity} = useAsyncAction({
        apiInfo: course_enrollment_resolveIdentity_Api,
        onSuccess: (result, meta) => {
            const updated = _.get(meta, 'response.data')
            if (updated) {
                setCurrentEnrollment(updated)
                setResolvingStudentId(null)
            }
        },
    })

    const {handleAsyncAction: assignClass, isLoading: assigningClass} = useAsyncAction({
        apiInfo: course_enrollment_assignClass_Api,
        onSuccess: (result, meta) => {
            const updated = _.get(meta, 'response.data')
            if (updated) setCurrentEnrollment(updated)
            onCompleted()
        },
    })

    const {handleAsyncAction: resetToPending, isLoading: resetting} = useAsyncAction({
        apiInfo: course_enrollment_resetToPending_Api,
        onSuccess: (result, meta) => {
            const updated = _.get(meta, 'response.data')
            if (updated) {
                setCurrentEnrollment(updated)
                setCandidates([])
                setSelectedClassId(null)
                triggeredRef.current.clear()
            }
        },
    })

    // ─── Auto-trigger pipeline ────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !enrollmentId) return
        const status = currentEnrollment?.status
        if (status === -1 && !triggeredRef.current.has('start_review')) {
            triggeredRef.current.add('start_review')
            startReview({}, {id: enrollmentId})
        } else if (status === 1 && !triggeredRef.current.has('search')) {
            triggeredRef.current.add('search')
            searchCandidates({}, {id: enrollmentId})
        }
    }, [open, currentEnrollment?.status, enrollmentId])

    if (!open || !enrollment) return null

    const enrollmentInfo = currentEnrollment ?? {}
    const canReset = currentEnrollment?.status !== -1

    // ─── Reset dropdown menu ──────────────────────────────────────────────────
    const resetMenu = {
        items: [
            {
                key: 'reset',
                icon: <BsArrowCounterclockwise size={13}/>,
                label: (
                    <span className="text-orange-500 font-medium">
                        {t('reset to pending')}
                    </span>
                ),
                onClick: () => resetToPending({}, {id: enrollmentId}),
            },
        ],
    }

    // ─── Steps ────────────────────────────────────────────────────────────────

    const renderIdentityReviewStep = () => (
        <>
            {(startingReview || searching) && (
                <div className="flex flex-col items-center gap-2 py-8">
                    <Spin/>
                    <span className="text-sm text-color-400 italic">
                        {t('searching candidates')}
                    </span>
                </div>
            )}

            {!startingReview && !searching && candidates.length > 0 && (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col">
                        <span className="font-semibold text-secondary-600 tracking-wider whitespace-nowrap">
                            {t('candidate match')}
                        </span>
                        <div style={{ height: 3 }} className="mt-0.5 mb-1 w-20 bg-secondary-400 rounded-full"/>
                        <span className="text-xs text-color-300 tracking-wider">
                            {t('select the matching student or create new')}
                        </span>
                    </div>
                    {candidates.map((candidate, i) => (
                        <CandidateCard
                            key={i}
                            candidate={candidate}
                            loading={resolvingIdentity && resolvingStudentId === candidate?.student?.id}
                            onSelectExisting={(student) => {
                                setResolvingStudentId(student?.id)
                                resolveIdentity({student_id: student?.id}, {id: enrollmentId})
                            }}
                            onViewDetail={(student) => setViewDetailStudent(student)}
                        />
                    ))}
                </div>
            )}

            {!startingReview && !searching && candidates.length === 0 && currentStep === 'identity_review' && (
                <div className="flex flex-col items-center gap-2 py-6 text-center">
                    <span className="text-sm text-color-400 italic">{t('no candidates found')}</span>
                    <span className="text-xs text-color-400">{t('you can create a new student record')}</span>
                </div>
            )}

            {!startingReview && currentStep === 'identity_review' && (
                <div className="pt-2 border-t border-color-50">
                    <Button
                        block={true}
                        loading={resolvingIdentity && resolvingStudentId === null}
                        color="primary" variant="filled"
                        onClick={() => {
                            setResolvingStudentId(null)
                            resolveIdentity({create_new: true}, {id: enrollmentId})
                        }}
                        className="font-semibold rounded-lg">
                        {t('create new student')}
                    </Button>
                </div>
            )}
        </>
    )

    const renderAssignClassStep = () => (
        <>
            {/* Linked student info */}
            {currentEnrollment?.student && (
                <div className="flex flex-col gap-1 p-3 rounded-lg bg-green-50 border border-green-200">
                    <span className="text-2xs font-semibold text-green-600 uppercase tracking-wider">
                        {t('linked student')}
                    </span>
                    <span className="font-bold text-sm text-color-000">
                        {currentEnrollment.student.fullname}
                    </span>
                    {currentEnrollment.student.student_idcode && (
                        <span className="text-xs font-mono text-blue-500">
                            #{currentEnrollment.student.student_idcode}
                        </span>
                    )}
                </div>
            )}

            <div className="flex flex-col gap-2">
                <div className="flex flex-col">
                    <span className="font-semibold text-secondary-600 tracking-wider whitespace-nowrap">
                        {t('select course class')}
                    </span>
                    <div style={{ height: 3 }} className="mt-0.5 mb-1 w-20 bg-secondary-400 rounded-full"/>
                    <span className="text-xs text-color-300 tracking-wider">
                        {t('assign student to a class to complete enrollment')}
                    </span>
                </div>
                <SelectWidget
                    className="w-full"
                    size="large"
                    placeholder={t('select course class')}
                    value={selectedClassId}
                    onChange={setSelectedClassId}
                    options={courseClasses.map((c) => ({value: c.id, label: c.title}))}
                />
            </div>

            <Button
                type="primary"
                size="large"
                block={true}
                disabled={!selectedClassId}
                loading={assigningClass}
                onClick={() => assignClass({course_class_id: selectedClassId}, {id: enrollmentId})}>
                {t('assign class')}
            </Button>
        </>
    )

    const renderCompletedStep = () => (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <IoCheckmarkCircle size={52} className="text-green-500"/>
            <div className="flex flex-col gap-1">
                <span className="font-bold text-base text-color-000">{t('enrollment completed')}</span>
                <span className="text-sm text-color-400 italic">
                    {currentEnrollment?.student?.fullname
                        ? `${currentEnrollment.student.fullname} ${t('has been assigned to class')}`
                        : t('student has been assigned to class')}
                </span>
            </div>
            <Button onClick={onClose}>{t('close')}</Button>
        </div>
    )

    // ─── Header ───────────────────────────────────────────────────────────────

    const drawerTitle = (
        <div className="pl-4 pr-3 w-full flex items-start gap-3">
            <EnrollmentStepProgress status={currentEnrollment?.status}/>
            <div className="flex items-center justify-between gap-2">
                {canReset && (
                    <Dropdown
                        menu={resetMenu}
                        trigger={['click']}
                        placement="bottomRight">
                        <button
                            type="button"
                            className="p-1 flex flex-center rounded-full hover:background-100 text-color-400 hover:text-color-100 transition-all duration-300"
                            disabled={resetting}>
                            <MdMoreVert size={18}/>
                        </button>
                    </Dropdown>
                )}
            </div>
        </div>
    )

    return (
        <>
            <StudentDetailModal
                student={viewDetailStudent}
                open={!!viewDetailStudent}
                onClose={() => setViewDetailStudent(null)}
            />
            <FullSideModalLayout
                right={true}
                title={
                    <span className="font-bold text-color-000 truncate">
                        {`${t('review enrollment')} — ${enrollmentInfo.fullname || ''}`}
                    </span>
                }
                onCancel={onClose}>
                {drawerTitle}
                <ReviewLayout
                    infoPanel={<EnrollmentInfoPanel enrollment={enrollmentInfo}/>}
                    actionPanel={
                        currentStep === 'identity_review' ? renderIdentityReviewStep() :
                            currentStep === 'assign_class' ? renderAssignClassStep() :
                                renderCompletedStep()
                    }
                />
            </FullSideModalLayout>
        </>
    )
}
